import { useEffect, useRef, useState } from "react";
import DevelopmentSelectMulti from "../selectComponents/developmentSelect/DevelopmentSelectMulti";
import BlockSelectMulti from "../selectComponents/blockSelect/BlockSelectMulti";
import translations from "utils/translationUtility";
import ClientSelectMulti from "../selectComponents/clientSelect/ClientSelectMulti";

interface DevBlockClientFilterProps {
  onDevelopmentChange: (developmentIDs: string[]) => void;
  onBlockChange: (blockIDs: string[]) => void;
  onClientChange: (clientIDs: string[]) => void;
  formKey?: number;
  className?: string;
  serviceChargeOnly?: boolean;
  defaultDevelopmentIDs?: string[];
  defaultBlockIDs?: string[];
  defaultClientIDs?: string[];
}

const DevBlockClientFilter: React.FC<DevBlockClientFilterProps> = ({
  onDevelopmentChange,
  onBlockChange,
  onClientChange,
  formKey = 0,
  className = "",
  serviceChargeOnly = false,
  defaultDevelopmentIDs,
  defaultBlockIDs,
  defaultClientIDs,
}) => {
  const [selectedDevelopmentIDs, setSelectedDevelopmentIDs] = useState<string[]>([]);
  const [selectedBlockIDs, setSelectedBlockIDs] = useState<string[]>([]);
  const [selectedClientIDs, setSelectedClientIDs] = useState<string[]>([]);
  const isInitialMount = useRef(true);
  const previousFormKey = useRef(formKey);

  const hasDevelopmentSelected = selectedDevelopmentIDs.length > 0;

  const handleDevelopmentChange = (selection: string[]) => {
    setSelectedDevelopmentIDs(selection);

    if (selectedBlockIDs.length > 0) {
      setSelectedBlockIDs([]);
      onBlockChange([]);
    }
    if (selectedClientIDs.length > 0) {
      setSelectedClientIDs([]);
      onClientChange([]);
    }

    onDevelopmentChange(selection);
  };

  const handleBlockChange = (selection: string[]) => {
    setSelectedBlockIDs(selection);

    if (selectedClientIDs.length > 0) {
      setSelectedClientIDs([]);
      onClientChange([]);
    }

    onBlockChange(selection);
  };

  const handleClientChange = (selection: string[]) => {
    setSelectedClientIDs(selection);
    onClientChange(selection);
  };

  useEffect(() => {
    if (isInitialMount.current) {
      if (defaultDevelopmentIDs?.length) {
        setSelectedDevelopmentIDs(defaultDevelopmentIDs);
        onDevelopmentChange(defaultDevelopmentIDs);
      }
      if (defaultBlockIDs?.length) {
        setSelectedBlockIDs(defaultBlockIDs);
        onBlockChange(defaultBlockIDs);
      }
      if (defaultClientIDs?.length) {
        setSelectedClientIDs(defaultClientIDs);
        onClientChange(defaultClientIDs);
      }
      isInitialMount.current = false;
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (previousFormKey.current !== formKey) {
      setSelectedDevelopmentIDs([]);
      setSelectedBlockIDs([]);
      setSelectedClientIDs([]);
      isInitialMount.current = true;
      previousFormKey.current = formKey;
    }
  }, [formKey]);

  return (
    <div className={`d-flex gap-5 ${className}`}>
      <div className="d-flex flex-column gap-2">
        <label>Development(s)</label>
        <DevelopmentSelectMulti
          data-test-id="development-block-client-filter__developments-select"
          key={`dev-${formKey}`}
          defaultAll={false}
          handleChange={handleDevelopmentChange}
          isServiceChargeOnly={serviceChargeOnly}
          defaultSome={isInitialMount.current ? defaultDevelopmentIDs : undefined}
        />
      </div>

      <div className="d-flex flex-column gap-2">
        <label>Block(s)</label>
        <BlockSelectMulti
          data-test-id="development-block-client-filter__blocks-select"
          key={`block-${formKey}`}
          defaultAll={false}
          handleChange={handleBlockChange}
          developmentIDs={selectedDevelopmentIDs}
          disabled={!hasDevelopmentSelected}
          defaultSome={isInitialMount.current ? defaultBlockIDs : undefined}
        />
      </div>

      <div className="d-flex flex-column gap-2">
        <label>{translations.client}(s)</label>
        <ClientSelectMulti
          data-test-id="development-block-client-filter__clients-select"
          key={`client-${formKey}`}
          defaultAll={false}
          handleChange={handleClientChange}
          blockIDs={selectedBlockIDs}
          developmentIDs={selectedDevelopmentIDs}
          disabled={!hasDevelopmentSelected}
          defaultSome={isInitialMount.current ? defaultClientIDs : undefined}
        />
      </div>
    </div>
  );
};

export default DevBlockClientFilter;
